/*
 * Design: Gradient Noir – Services overview page
 * All services use the original large core service card design:
 * glass-card with image header, gradient icon box, large title, description, arrow link
 */
import { Link } from "wouter";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Globe, Search, Megaphone, Share2, Bot, Shield, ArrowLeft, ExternalLink } from "lucide-react";

function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const allServices = [
  {
    icon: Globe,
    title: "בניית אתרים",
    desc: "פיתוח אתרים מודרניים, מהירים ומותאמים לקידום ולהמרות. אנחנו בונים אתרים שעובדים בשבילכם.",
    href: "/services/web-development",
    image: "/images/services/web-development.jpeg",
  },
  {
    icon: Search,
    title: "קידום אורגני (SEO)",
    desc: "הגדלת חשיפה בגוגל באמצעות אסטרטגיית SEO מקצועית שמייצרת תנועה ולידים לאורך זמן.",
    href: "/services/seo",
    image: "/images/services/seo.jpeg",
  },
  {
    icon: Megaphone,
    title: "קידום ממומן",
    desc: "ניהול קמפיינים בגוגל וברשתות החברתיות עם מיקוד ב-ROI ותוצאות מדידות.",
    href: "/services/paid-advertising",
    image: "/images/services/paid-advertising.jpeg",
  },
  {
    icon: Share2,
    title: "ניהול סושיאל",
    desc: "ניהול תוכן ונוכחות ברשתות החברתיות שמחזק את המותג ומייצר מעורבות.",
    href: "/services/social-media",
    image: "/images/services/social-media.jpeg",
  },
  {
    icon: Bot,
    title: "קידום באמצעות AI",
    desc: "שימוש בכלי AI מתקדמים לייעול תהליכי שיווק, יצירת תוכן וצמיחה מואצת.",
    href: "/services/ai-marketing",
    image: "/images/services/ai-marketing.jpeg",
  },
  {
    icon: Shield,
    title: "ניהול מוניטין",
    desc: "ניהול ושיפור המוניטין הדיגיטלי של העסק שלכם – הגנה על התדמית המקוונת וטיפול בתוכן שלילי.",
    href: "https://upsiteorm.co.il/",
    external: true,
    image: "/images/services/reputation-management.jpeg",
  },
];

interface ServiceCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  href: string;
  image?: string;
  external?: boolean;
  delay: number;
}

function ServiceCard({ icon: Icon, title, desc, href, image, external, delay }: ServiceCardProps) {
  const cardContent = (
    <div className="glass-card overflow-hidden group hover:-translate-y-2 transition-all duration-300 border-[#66CDAA]/15 hover:border-[#66CDAA]/30 hover:shadow-[0_20px_60px_rgba(102,205,170,0.15)] h-full flex flex-col">
      {/* Image header – show generated image or a gradient placeholder */}
      <div className="aspect-video overflow-hidden relative">
        {image ? (
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0F172A] via-[#1a2744] to-[#0d2137] flex items-center justify-center relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(102,205,170,0.08),transparent_70%)]" />
            <Icon className="w-16 h-16 text-[#66CDAA]/30 group-hover:text-[#66CDAA]/50 transition-colors duration-500" />
          </div>
        )}
      </div>
      {/* Card body */}
      <div className="p-8 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#66CDAA] to-[#3DD5B0] flex items-center justify-center shrink-0">
            <Icon className="w-6 h-6 text-[#0F172A]" />
          </div>
          <h3 className="text-2xl font-bold text-white font-[Rubik] group-hover:text-[#66CDAA] transition-colors">
            {title}
          </h3>
        </div>
        <p className="text-white/60 leading-relaxed flex-1 mb-4">{desc}</p>
        <span className="inline-flex items-center gap-2 text-[#66CDAA] font-medium">
          {external ? "לאתר השירות" : "למידע נוסף"}
          {external ? (
            <ExternalLink className="w-4 h-4" />
          ) : (
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          )}
        </span>
      </div>
    </div>
  );

  if (external) {
    return (
      <AnimatedSection delay={delay}>
        <a href={href} target="_blank" rel="noopener noreferrer" className="block h-full">
          {cardContent}
        </a>
      </AnimatedSection>
    );
  }

  return (
    <AnimatedSection delay={delay}>
      <Link href={href} className="block h-full">
        {cardContent}
      </Link>
    </AnimatedSection>
  );
}

export default function Services() {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#66CDAA]/10 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-[Rubik] leading-tight">
              <span className="gradient-text">השירותים</span> שלנו
            </h1>
            <p className="text-lg text-white/60 leading-relaxed max-w-2xl">
              פתרונות דיגיטליים מקצועיים שמותאמים לצרכים שלכם – מבניית אתרים ועד קידום אורגני, ממומן ו-AI.
            </p>
          </div>
        </div>
      </section>

      {/* All Services – Unified Grid */}
      <section className="py-20 lg:py-28 section-navy">
        <div className="container">
          <AnimatedSection>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-12 font-[Rubik]">
              כל <span className="gradient-text">השירותים</span> שלנו
            </h2>
          </AnimatedSection>

          <div className="grid lg:grid-cols-2 gap-8">
            {allServices.map((service, i) => (
              <ServiceCard
                key={service.href}
                icon={service.icon}
                title={service.title}
                desc={service.desc}
                href={service.href}
                image={service.image}
                external={'external' in service ? (service as { external?: boolean }).external : undefined}
                delay={i * 100}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="container relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-[Rubik]">
              לא בטוחים מה מתאים לכם?
            </h2>
            <p className="text-lg text-white/60 mb-8 max-w-xl mx-auto">
              דברו איתנו ונבנה יחד את התוכנית הדיגיטלית המושלמת לעסק שלכם.
            </p>
            <Link href="/contact" className="glow-btn inline-block text-lg px-10 py-4">
              דברו איתנו
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
